import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { RefreshCw, CheckCircle, AlertCircle, HelpCircle, Loader2, ChevronDown, ChevronUp, ExternalLink, Paperclip, Trash2, Zap } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from '../../i18n';
import { NavigationBar } from '../../components/ui/NavigationBar';
import { useCarBySlug, LOG_ENTRY_KEYS } from '../../hooks/useCarData';
import { ExpenseItem } from '../../components/cars/ExpenseItem';
import { usePreferences } from '../../contexts/PreferencesContext';
import { ImageViewer } from '../../components/ui/ImageViewer';
import { PdfViewer } from '../../components/ui/PdfViewer';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';

type IncomingEmail = {
    id: string;
    created_at: string;
    processed_at: string;
    from_email: string;
    subject: string;
    status: 'pending' | 'processed' | 'error';
    chatgpt_response: any;
    processing_error?: string;
    body_text?: string;
    body_html?: string;
    car_id?: string;
    to_email?: string;
    expenses?: {
        id: string;
        log_entry_id?: string;
        title: string;
        amount: number;
        date: string;
        type: string;
        description: string;
        fuel_liters: number;
        electricity_kwh: number;
        fuel_type: string;
        log_entries?: {
            location_id?: string;
            log_entry_parts?: { spare_part_id: string }[];
        };
    }[];
    attachments?: {
        id: string;
        file_name: string;
        storage_path: string;
        content_type: string;
    }[];
};

export function CarEmailsView() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslations();
    const { preferences } = usePreferences();
    const queryClient = useQueryClient();
    const { data: car, isLoading: carLoading } = useCarBySlug(slug as string);
    const { isAdmin } = useAuth();

    const effectivePreferences: any = car ? {
        distance_unit: car.distance_unit || (preferences as any)?.distance_unit,
        volume_unit: car.volume_unit || (preferences as any)?.volume_unit,
        currency: car.currency || (preferences as any)?.currency,
    } : preferences;

    const [emails, setEmails] = useState<IncomingEmail[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

    const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
    const [viewerImages, setViewerImages] = useState<string[] | null>(null);
    const [viewerIndex, setViewerIndex] = useState(0);

    const [emailToDelete, setEmailToDelete] = useState<IncomingEmail | null>(null);
    const [isDeleteAllConfirmOpen, setIsDeleteAllConfirmOpen] = useState(false);
    const [reparsingEmailId, setReparsingEmailId] = useState<string | null>(null);

    const fetchEmails = async () => {
        if (!car) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('incoming_emails')
                .select(`
                    id, created_at, processed_at, from_email, subject, status, 
                    chatgpt_response, processing_error, body_text, body_html, car_id, to_email,
                    expenses:expenses!expenses_source_email_id_fkey(
                        id, log_entry_id, title, amount, date, type, description, 
                        fuel_liters, electricity_kwh, fuel_type,
                        log_entries(
                            location_id,
                            log_entry_parts(spare_part_id)
                        )
                    ),
                    attachments: email_attachments(id, file_name, storage_path, content_type)
                `)
                .eq('car_id', car.id)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching emails:', error);
                throw error;
            }

            setEmails((data as any) || []);
            queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.list(car.id) });
        } catch (err) {
            console.error('Error fetching emails:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEmail = async () => {
        if (!emailToDelete || !car) return;
        try {
            const { error } = await supabase
                .from('incoming_emails')
                .delete()
                .eq('id', emailToDelete.id);

            if (error) throw error;
            setEmails(prev => prev.filter(e => e.id !== emailToDelete.id));
            setEmailToDelete(null);
        } catch (err) {
            console.error('Error deleting email:', err);
        }
    };

    const handleDeleteAll = async () => {
        if (!car) return;
        try {
            const { error } = await supabase
                .from('incoming_emails')
                .delete()
                .eq('car_id', car.id);

            if (error) throw error;
            setEmails([]);
            setIsDeleteAllConfirmOpen(false);
        } catch (err) {
            console.error('Error deleting all emails:', err);
        }
    };

    const handleReparse = async (emailId: string) => {
        if (!isAdmin) return;
        setReparsingEmailId(emailId);
        try {
            const resp = await fetch('https://my-car-email-processor.gtreulands.workers.dev/reparse', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emailId })
            });
            if (!resp.ok) throw new Error('Failed to start reparsing');
            
            // Wait a moment and then refresh
            setTimeout(fetchEmails, 2000);
        } catch (err) {
            console.error('Error reparsing:', err);
        } finally {
            setReparsingEmailId(null);
        }
    };

    useEffect(() => {
        if (car) {
            fetchEmails();
        }
    }, [car]);

    const toggleExpand = (id: string) => {
        setExpandedEmailId(prev => prev === id ? null : id);
    };

    const handleAttachmentClick = (e: React.MouseEvent, att: NonNullable<IncomingEmail['attachments']>[number], attachments: NonNullable<IncomingEmail['attachments']>) => {
        e.preventDefault();
        const url = `https://my-car-email-processor.gtreulands.workers.dev/file/${encodeURIComponent(att.storage_path)}`;
        const isPdf = att.file_name.toLowerCase().endsWith('.pdf') || att.content_type?.toLowerCase().includes('pdf');

        if (isPdf) {
            setPdfViewerUrl(url);
        } else {
            const imageAtts = attachments.filter(a => !a.file_name.toLowerCase().endsWith('.pdf') && !a.content_type?.toLowerCase().includes('pdf'));
            const images = imageAtts.map(a => `https://my-car-email-processor.gtreulands.workers.dev/file/${encodeURIComponent(a.storage_path)}`);
            const index = imageAtts.findIndex(a => a.id === att.id);
            setViewerImages(images);
            setViewerIndex(Math.max(0, index));
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'processed':
                return <CheckCircle size={18} className="text-green-500" />;
            case 'error':
                return <AlertCircle size={18} className="text-red-500" />;
            case 'pending':
                return <Loader2 size={18} className="text-blue-500 animate-spin" />;
            default:
                return <HelpCircle size={18} className="text-gray-400" />;
        }
    };

    const getAIResultText = (email: IncomingEmail) => {
        if (email.subject && email.subject.includes('Gmail Forwarding Confirmation')) {
            const codeMatch = email.body_text?.match(/Confirmation code:\s*([0-9]+)/);
            if (codeMatch) {
                const code = codeMatch[1];
                return (
                    <span className="text-orange-600 font-bold dark:text-orange-400">
                        Gmail Verification Code: <span className="text-xl mx-2 bg-orange-100 dark:bg-orange-900/40 px-2 rounded border border-orange-200">{code}</span>
                    </span>
                 );
            }

            const urlMatch = email.body_text?.match(/(https?:\/\/[^\s]+verification[^\s]+)/i) ||
                email.body_text?.match(/(https?:\/\/[^\s]+google\.com[^\s]+)/i);

            if (urlMatch) {
                return (
                    <span className="text-orange-600 font-bold dark:text-orange-400 flex items-center gap-2">
                        Gmail Verification:
                        <a href={urlMatch[0]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 underline text-blue-600 dark:text-blue-400">
                            Click to Verify <ExternalLink size={14} />
                        </a>
                    </span>
                );
            }

            return <span className="text-orange-600 font-bold dark:text-orange-400">Gmail Verification: Code/Link not found (Expand to see email)</span>;
        }

        if (email.status === 'error') return email.processing_error || t.common.error;
        if (email.status === 'pending') return t.common.processing;

        if (email.expenses && email.expenses.length > 0) {
            return (
                <div className="flex flex-col gap-1 mt-1">
                    <span className="text-xs text-gray-500 mb-1">{t.expenses.expensesFound}:</span>
                    {email.expenses.map(exp => (
                        <ExpenseItem
                            key={exp.id}
                            expense={{
                                ...exp,
                                id: exp.log_entry_id || exp.id,
                                imageCount: email.attachments?.length || 0,
                                partCount: exp.log_entries?.log_entry_parts?.length || 0,
                                location_id: exp.log_entries?.location_id
                            } as any}
                            slug={car?.slug || slug || ''}
                            effectivePreferences={effectivePreferences}
                            className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-white/10 rounded-xl"
                        />
                    ))}
                </div>
            );
        }

        if (email.chatgpt_response) {
            try {
                const response = typeof email.chatgpt_response === 'string'
                    ? JSON.parse(email.chatgpt_response)
                    : email.chatgpt_response;

                if (response.expenses && response.expenses.length > 0) {
                    if (email.status === 'processed') {
                        return `${response.expenses.length} - ${t.expenses.expensesFound} (Deleted)`;
                    }
                    return `${response.expenses.length} - ${t.expenses.expensesFound} (Processing...)`;
                }
                return t.expenses.noExpensesFound;
            } catch (e) {
                return t.common.error;
            }
        }
        return "-";
    };

    const renderEmailItem = (email: IncomingEmail) => (
        <div key={email.id} className="p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-white/5 space-y-3 relative group">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 cursor-pointer" onClick={(e) => {
                    e.preventDefault();
                    toggleExpand(email.id);
                }}>
                    <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {email.subject || '(No Subject)'}
                        </p>
                        {email.attachments && email.attachments.length > 0 && (
                            <span className="flex shrink-0 items-center justify-center gap-1 text-[10px] font-bold bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-md">
                                <Paperclip size={10} />
                                {email.attachments.length}
                            </span>
                        )}
                    </div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(email.created_at).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{email.from_email}</p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                    {isAdmin && (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleReparse(email.id);
                            }}
                            disabled={reparsingEmailId === email.id}
                            title="Re-parse (Admin only)"
                            className={`p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all ${reparsingEmailId === email.id ? 'animate-pulse' : ''}`}
                        >
                            <Zap size={16} fill={reparsingEmailId === email.id ? 'currentColor' : 'none'} />
                        </button>
                    )}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEmailToDelete(email);
                        }}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                    >
                        <Trash2 size={16} />
                    </button>
                    {getStatusIcon(email.status)}
                    <div 
                        className="p-1 text-gray-400 cursor-pointer" 
                        onClick={() => toggleExpand(email.id)}
                    >
                        {expandedEmailId === email.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                </div>
            </div>

            {/* AI Result / Status Message */}
            <div className="pt-3 border-t border-gray-200 dark:border-white/5">
                <div className="text-xs text-gray-600 dark:text-gray-300">
                    {getAIResultText(email)}
                </div>
            </div>

            {/* Expanded Content */}
            {expandedEmailId === email.id && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/5 space-y-4">
                    <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border border-gray-200 dark:border-white/10 overflow-auto max-h-96 text-xs text-gray-800 dark:text-gray-200">
                        {email.body_html ? (
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html) }} />
                        ) : (
                            <pre className="whitespace-pre-wrap font-sans">{email.body_text || '(No Content)'}</pre>
                        )}
                    </div>
                    {email.attachments && email.attachments.length > 0 && (
                        <div>
                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2 block">{(t.common as any).attachments || 'Attachments'}:</span>
                            <div className="flex flex-wrap gap-2">
                                {email.attachments.map(att => (
                                    <button
                                        key={att.id}
                                        type="button"
                                        onClick={(e) => handleAttachmentClick(e, att, email.attachments!)}
                                        className="flex items-center gap-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 px-3 py-1.5 rounded-lg text-xs font-medium hover:border-blue-500 hover:text-blue-500 transition-colors"
                                    >
                                        <Paperclip size={14} />
                                        <span className="truncate max-w-[150px]">{att.file_name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {isAdmin && email.chatgpt_response && (
                        <div>
                            <span className="text-xs font-bold text-blue-500 dark:text-blue-400 mb-2 block font-mono uppercase tracking-wider">DEBUG: OpenAI/ChatGPT Response:</span>
                            <pre className="bg-slate-900 text-blue-300 p-3 rounded-xl border border-blue-500/20 text-[10px] overflow-auto max-h-60 font-mono">
                                {typeof email.chatgpt_response === 'string' 
                                    ? JSON.stringify(JSON.parse(email.chatgpt_response), null, 2) 
                                    : JSON.stringify(email.chatgpt_response, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    if (carLoading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-gradient-theme">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400" />
            </div>
        );
    }

    if (!car) {
        return null;
    }

    return (
        <div className="fixed inset-0 flex flex-col z-[100] text-gray-900 dark:text-gray-100 bg-gradient-theme">
            <div className="flex-shrink-0 z-30 relative">
                <NavigationBar
                    title={t.cars.recentEmails}
                    onBack={() => navigate(-1)}
                    fixed={false}
                    rightAction={[
                        {
                            icon: RefreshCw,
                            onClick: fetchEmails,
                            className: loading ? 'animate-spin' : ''
                        },
                        emails.length > 0 ? {
                            icon: Trash2,
                            onClick: () => setIsDeleteAllConfirmOpen(true),
                            className: 'text-red-500'
                        } : null
                    ].filter(Boolean) as any}
                />
            </div>

            <div className="flex-1 overflow-y-auto w-full">
                <div className="max-w-2xl mx-auto pb-20 px-2 pt-4">
                    {emails.length === 0 ? (
                        <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl p-8 text-center mt-4">
                            <p className="text-sm text-gray-500 italic py-4">{(t.landing as any).emails?.noEmails || t.cars.noEmailsReceived}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {emails.map(renderEmailItem)}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={!!emailToDelete}
                onCancel={() => setEmailToDelete(null)}
                onConfirm={handleDeleteEmail}
                title={(t.landing as any).emails?.deleteConfirm || "Delete Email"}
                message={(t.landing as any).emails?.deleteConfirm || "Are you sure?"}
                confirmLabel={t.common.delete}
                cancelLabel={t.common.cancel}
                isDestructive={true}
            />

            <ConfirmModal
                isOpen={isDeleteAllConfirmOpen}
                onCancel={() => setIsDeleteAllConfirmOpen(false)}
                onConfirm={handleDeleteAll}
                title={(t.landing as any).emails?.deleteAll || "Delete All"}
                message={(t.landing as any).emails?.deleteAllConfirm || "Are you sure you want to delete all?"}
                confirmLabel={t.common.delete}
                cancelLabel={t.common.cancel}
                isDestructive={true}
            />

            {viewerImages && (
                <ImageViewer
                    images={viewerImages}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerImages(null)}
                />
            )}
            {pdfViewerUrl && (
                <PdfViewer
                    url={pdfViewerUrl}
                    onClose={() => setPdfViewerUrl(null)}
                />
            )}
        </div>
    );
}
